import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token and viewing company to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // If super admin is viewing a company, add the header
  const viewingCompany = localStorage.getItem('viewingCompany');
  if (viewingCompany) {
    try {
      const company = JSON.parse(viewingCompany);
      if (company?.id) {
        config.headers['X-Viewing-Company-Id'] = company.id;
      }
    } catch (e) {
      // Ignore JSON parse errors
    }
  }
  
  return config;
});

// Handle auth errors + server redeploy (Cloudflare) errors
// Bij tijdelijke fouten: GET-requests 1x stil opnieuw proberen (zonder melding).
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(error);
    }
    const status = error.response?.status;
    const isHtmlBody = (typeof error.response?.data === 'string' && error.response.data.trim().toLowerCase().startsWith('<'))
      || (error.response?.data instanceof Blob && (error.response.data.type || '').includes('text/html'));
    const transient = !error.response || [502, 503, 504, 520, 521, 522, 523, 524].includes(status) || isHtmlBody;
    if (transient) {
      const cfg = error.config || {};
      if ((cfg.method || 'get').toLowerCase() === 'get' && !cfg._retried) {
        cfg._retried = true;
        await new Promise(r => setTimeout(r, 1500));
        return api.request(cfg);
      }
      // Geen "Server wordt bijgewerkt" melding meer — gaf te vaak vals alarm
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
};

// Customer APIs
export const customerAPI = {
  getAll: (params) => api.get('/customers', { params }),
  getOne: (id) => api.get(`/customers/${id}`),
  getDetails: (id) => api.get(`/customers/${id}/details`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  // Generic request helper for email endpoints
  request: (method, url, data = null) => api[method](url, data),
};

// Property APIs
export const propertyAPI = {
  getAll: (params) => api.get('/properties', { params }),
  getOne: (id) => api.get(`/properties/${id}`),
  getDetails: (id) => api.get(`/properties/${id}/details`),
  create: (data) => api.post('/properties', data),
  update: (id, data) => api.put(`/properties/${id}`, data),
  delete: (id) => api.delete(`/properties/${id}`),
  updateStatus: (id, statusField, value) => api.patch(`/properties/${id}/status`, null, { 
    params: { status_field: statusField, value } 
  }),
};

// Installation APIs
export const installationAPI = {
  getAll: (params) => api.get('/installations', { params }),
  getOne: (id) => api.get(`/installations/${id}`),
  create: (data) => api.post('/installations', data),
  update: (id, data) => api.put(`/installations/${id}`, data),
  delete: (id) => api.delete(`/installations/${id}`),
};

// Scheduled Installations API (for agenda)
export const scheduledInstallationAPI = {
  getAll: (params) => api.get('/scheduled-installations', { params }),
  getOne: (id) => api.get(`/scheduled-installations/${id}`),
  create: (data) => api.post('/scheduled-installations', data),
  update: (id, data) => api.put(`/scheduled-installations/${id}`, data),
  delete: (id) => api.delete(`/scheduled-installations/${id}`),
};

// Maintenance APIs
export const maintenanceAPI = {
  getAll: (params) => api.get('/maintenance', { params }),
  create: (data) => api.post('/maintenance', data),
  delete: (id) => api.delete(`/maintenance/${id}`),
};

// Cleaning APIs
export const cleaningAPI = {
  getAll: (params) => api.get('/cleaning', { params }),
  create: (data) => api.post('/cleaning', data),
  update: (id, data) => api.put(`/cleaning/${id}`, data),
  delete: (id) => api.delete(`/cleaning/${id}`),
  sendConfirmationEmail: (data) => api.post('/webhook/cleaning-confirmation', data),
  markComplete: (id) => api.patch(`/cleaning/${id}/complete`),
  markCancelled: (id, reason) => api.patch(`/cleaning/${id}/cancel`, null, { params: { reason } }),
  reschedule: (id) => api.patch(`/cleaning/${id}/reschedule`),
};

// Cleaner-specific APIs (limited access for cleaning staff)
export const cleanerAPI = {
  getAppointments: () => api.get('/cleaner/appointments'),
  getProperty: (propertyId) => api.get(`/cleaner/property/${propertyId}`),
  uploadPhoto: (propertyId, file, description, cleaningId = null) => {
    const formData = new FormData();
    formData.append('property_id', propertyId);
    formData.append('file', file);
    if (description) formData.append('description', description);
    if (cleaningId) formData.append('cleaning_id', cleaningId);
    return api.post('/cleaner/upload-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Offerte/Quote API
export const offerteAPI = {
  createElektriciteit: (propertyId, file) => {
    const formData = new FormData();
    formData.append('property_id', propertyId);
    formData.append('file', file);
    return api.post('/offerte/elektriciteit', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Cleaning offertes
  getAll: (params) => api.get('/offertes', { params }),
  getOne: (id) => api.get(`/offertes/${id}`),
  create: (data) => api.post('/offertes', data),
  update: (id, data) => api.put(`/offertes/${id}`, data),
  delete: (id) => api.delete(`/offertes/${id}`),
  updateStatus: (id, status) => api.post(`/offertes/${id}/status`, null, { params: { status } }),
  downloadPdf: (id) => api.get(`/offertes/${id}/pdf`, { responseType: 'blob' }),
  exportToDrive: (id) => api.post(`/offertes/${id}/export-drive`),
};

// Offerte Template APIs
export const offerteTemplateAPI = {
  getAll: (templateType) => api.get('/offerte-templates', { params: { template_type: templateType } }),
  getOne: (id) => api.get(`/offerte-templates/${id}`),
  create: (data) => api.post('/offerte-templates', data),
  delete: (id) => api.delete(`/offerte-templates/${id}`),
};

// Factuur (Invoice) APIs
export const factuurAPI = {
  getAll: () => api.get('/facturen'),
  getOne: (id) => api.get(`/facturen/${id}`),
  create: (data) => api.post('/facturen', data),
  update: (id, data) => api.put(`/facturen/${id}`, data),
  delete: (id) => api.delete(`/facturen/${id}`),
  downloadPdf: (id) => api.get(`/facturen/${id}/pdf`, { responseType: 'blob' }),
  exportToDrive: (id) => api.post(`/facturen/${id}/export-drive`),
  updateStatus: (id, status) => api.post(`/facturen/${id}/status?status=${status}`),
  addPayment: (id, data) => api.post(`/facturen/${id}/payments`, data),
  deletePayment: (id, paymentId) => api.delete(`/facturen/${id}/payments/${paymentId}`),
  updateInternalNote: (id, note) => api.put(`/facturen/${id}/internal-note`, { note }),
};

// Document APIs
export const documentAPI = {
  getAll: (params) => api.get('/documents', { params }),
  upload: (formData) => api.post('/documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (id) => api.delete(`/documents/${id}`),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getInstallationsByYear: () => api.get('/dashboard/charts/installations-by-year'),
  getInstallationsByCity: () => api.get('/dashboard/charts/installations-by-city'),
};

// Sales Opportunities API
export const opportunitiesAPI = {
  getAll: () => api.get('/opportunities'),
};

// CSV Import APIs
export const csvAPI = {
  preview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/csv/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  checkDuplicates: (data) => api.post('/csv/check-duplicates', data),
  import: (data) => api.post('/csv/import', data),
};

// Global Search API
export const searchAPI = {
  search: (q) => api.get('/search', { params: { q } }),
};

// Appointments/Agenda API
export const appointmentAPI = {
  getAll: (params) => api.get('/appointments', { params }),
  getOne: (id) => api.get(`/appointments/${id}`),
  getCalendar: (year, month) => api.get(`/appointments/calendar/${year}/${month}`),
  create: (data) => api.post('/appointments', data),
  update: (id, data) => api.put(`/appointments/${id}`, data),
  delete: (id) => api.delete(`/appointments/${id}`),
};

// AI Chat API
export const chatAPI = {
  send: (message, conversationId) => api.post('/chat', { message, conversation_id: conversationId }),
  getHistory: (conversationId) => api.get(`/chat/history/${conversationId}`),
  clearHistory: (conversationId) => api.delete(`/chat/history/${conversationId}`),
};

// Google Calendar API
export const googleCalendarAPI = {
  getLoginUrl: () => api.get('/oauth/calendar/login'),
  getStatus: () => api.get('/oauth/calendar/status'),
  disconnect: () => api.post('/oauth/calendar/disconnect'),
  syncEvent: (eventData) => api.post('/calendar/sync-to-google', eventData),
  deleteEvent: (eventId) => api.delete(`/calendar/google-event/${eventId}`),
  getGoogleEvents: () => api.get('/calendar/google-events'),
};

// Admin API
export const adminAPI = {
  // Users
  getUsers: () => api.get('/admin/users'),
  getUserActivity: (userId, limit = 100) => api.get(`/admin/users/${userId}/activity`, { params: { limit } }),
  updateUserRole: (userId, newRole) => api.put(`/admin/users/${userId}/role`, null, { params: { new_role: newRole } }),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  
  // Activity logs
  getActivity: (params) => api.get('/admin/activity', { params }),
  logActivity: (action, entityType, entityId, details) => api.post('/admin/activity/log', null, { 
    params: { action, entity_type: entityType, entity_id: entityId, details } 
  }),
  
  // Snapshots
  getSnapshots: () => api.get('/admin/snapshots'),
  createSnapshot: (description) => api.post('/admin/snapshots', null, { params: { description } }),
  restoreSnapshot: (snapshotId) => api.post(`/admin/snapshots/${snapshotId}/restore`),
  deleteSnapshot: (snapshotId) => api.delete(`/admin/snapshots/${snapshotId}`),
  
  // Stats
  getStats: () => api.get('/admin/stats'),
  
  // Time-based restore
  getRestoreOptions: () => api.get('/admin/restore/options'),
  restoreByTime: (minutesAgo) => api.post('/admin/restore/by-time', null, { params: { minutes_ago: minutesAgo } }),
};

// Notifications API
export const notificationAPI = {
  getAll: (unreadOnly = false, limit = 50, extraParams = {}) => api.get('/notifications', { params: { unread_only: unreadOnly, limit, ...extraParams } }),
  getCount: () => api.get('/notifications/count'),
  markRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markAllRead: (params = {}) => api.put('/notifications/read-all', null, { params }),
  delete: (notificationId) => api.delete(`/notifications/${notificationId}`),
  checkDuplicates: () => api.post('/notifications/check-duplicates'),
  checkDataErrors: () => api.post('/notifications/check-data-errors'),
  previewMerge: () => api.get('/notifications/duplicate-preview'),
  autoMergeDuplicates: () => api.post('/notifications/auto-merge-duplicates'),
};

// Aanvragen/Leads API
export const aanvragenAPI = {
  getAll: (params = {}) => api.get('/aanvragen', { params }),
  create: (data) => api.post('/aanvragen', data),
  updateStatus: (id, status) => api.put(`/aanvragen/${id}/status`, null, { params: { status } }),
  updateMessage: (id, message) => api.put(`/aanvragen/${id}/message`, { message }),
  assign: (id, assignedTo) => api.put(`/aanvragen/${id}/assign`, { assigned_to: assignedTo }),
  unassign: (id) => api.put(`/aanvragen/${id}/unassign`),
  delete: (id) => api.delete(`/aanvragen/${id}`),
  convert: (id, customerData = null) => api.post(`/aanvragen/${id}/convert`, customerData),
  createOfferte: (id) => api.post(`/aanvragen/${id}/create-offerte`),
};

export const salesAPI = {
  getTeamOverview: () => api.get('/sales/team-overview'),
  getUsers: () => api.get('/sales/users'),
};

// Creditnota API
export const creditnotaAPI = {
  getAll: () => api.get('/creditnotas'),
  create: (data) => api.post('/creditnotas', data),
  update: (id, data) => api.put(`/creditnotas/${id}`, data),
  delete: (id) => api.delete(`/creditnotas/${id}`),
  downloadPdf: (id) => api.get(`/creditnotas/${id}/pdf`, { responseType: 'blob' }),
};

// Geocoding API
export const geocodeAPI = {
  geocodeAddress: (street, number, city, postalCode) => 
    api.get('/geocode', { params: { street, number, city, postal_code: postalCode } }),
  geocodeBatch: (propertyIds) => 
    api.post('/geocode/batch', propertyIds),
  verifyAll: () => api.post('/geocode/verify-all'),
  verifyStatus: () => api.get('/geocode/verify-status'),
};

// Company Settings API
export const companySettingsAPI = {
  get: () => api.get('/company-settings'),
  update: (data) => api.put('/company-settings', data),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/company-settings/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Installation Products API (for product catalog)
export const installationProductAPI = {
  getAll: (params) => api.get('/installation-products', { params }),
  getOne: (id) => api.get(`/installation-products/${id}`),
  create: (data) => api.post('/installation-products', data),
  update: (id, data) => api.put(`/installation-products/${id}`, data),
  delete: (id, permanent = true) => api.delete(`/installation-products/${id}?permanent=${permanent}`),
  seedElektriciteit: () => api.post('/products/seed-elektriciteit'),
};

// Installation Templates API
export const installationTemplateAPI = {
  getAll: () => api.get('/installation-templates'),
  getOne: (id) => api.get(`/installation-templates/${id}`),
  create: (data) => api.post('/installation-templates', data),
  update: (id, data) => api.put(`/installation-templates/${id}`, data),
  delete: (id) => api.delete(`/installation-templates/${id}`),
};

// Installation Offertes API
export const installationOfferteAPI = {
  getAll: (params) => api.get('/installation-offertes', { params }),
  getOne: (id) => api.get(`/installation-offertes/${id}`),
  create: (data) => api.post('/installation-offertes', data),
  update: (id, data) => api.put(`/installation-offertes/${id}`, data),
  delete: (id) => api.delete(`/installation-offertes/${id}`),
  updateStatus: (id, status) => api.post(`/installation-offertes/${id}/status`, null, { params: { status } }),
  getPdf: (id) => api.get(`/installation-offertes/${id}/pdf`, { responseType: 'blob' }),
  exportToDrive: (id) => api.post(`/installation-offertes/${id}/export-drive`),
  getInvoicedTerms: (id) => api.get(`/installation-offertes/${id}/invoiced-terms`),
};

// Installation Facturen API
export const installationFactuurAPI = {
  getAll: (params) => api.get('/installation-facturen', { params }),
  getOne: (id) => api.get(`/installation-facturen/${id}`),
  create: (data) => api.post('/installation-facturen', data),
  update: (id, data) => api.put(`/installation-facturen/${id}`, data),
  delete: (id) => api.delete(`/installation-facturen/${id}`),
  updateStatus: (id, status) => api.post(`/installation-facturen/${id}/status`, null, { params: { status } }),
  addPayment: (id, data) => api.post(`/installation-facturen/${id}/payments`, data),
  deletePayment: (id, paymentId) => api.delete(`/installation-facturen/${id}/payments/${paymentId}`),
  updateInternalNote: (id, note) => api.put(`/installation-facturen/${id}/internal-note`, { note }),
  createFromOfferte: (offerteId) => api.post(`/installation-facturen/from-offerte/${offerteId}`),
  createTermFromOfferte: (offerteId, termNumber, percentage) => 
    api.post(`/installation-facturen/from-offerte/${offerteId}/term/${termNumber}`, null, { 
      params: percentage ? { percentage } : {} 
    }),
  getPdf: (id) => api.get(`/installation-facturen/${id}/pdf`, { responseType: 'blob' }),
  exportToDrive: (id) => api.post(`/installation-facturen/${id}/export-drive`),
  // Payment terms
  payTerm: (id, termNumber) => api.post(`/installation-facturen/${id}/pay-term/${termNumber}`),
  unpayTerm: (id, termNumber) => api.post(`/installation-facturen/${id}/unpay-term/${termNumber}`),
  updatePaymentTerms: (id, paymentTerms) => api.put(`/installation-facturen/${id}/payment-terms`, paymentTerms),
};

// Stock / Voorraad API
export const stockAPI = {
  getAll: (lowStockOnly = false) => api.get('/stock', { params: { low_stock_only: lowStockOnly } }),
  getOne: (productId) => api.get(`/stock/${productId}`),
  createOrUpdate: (data) => api.post('/stock', data),
  recordMovement: (data) => api.post('/stock/movement', data),
  getMovements: (productId, limit = 50) => api.get(`/stock/movements/${productId}`, { params: { limit } }),
  getSummary: () => api.get('/stock/summary'),
  getAlerts: () => api.get('/stock/alerts'),
  exportStock: () => api.get('/stock/export'),
  deductForInstallation: (installationId) => api.post(`/stock/deduct-for-installation?installation_id=${installationId}`),
};

export default api;
